import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {type CreateTriggerDrawerInit} from './EventStore';
import {EntryPoint} from './types';

/*
This file contains the business logic for state that spans across all parts of the CreateTriggerDrawer (CTD).
*/

export enum Steps {
  BASIC_METRIC_NOTIFICATION,
  EVENT_CONFIGURATION,
  ACTION_CONFIGURATION,
  SUMMARY,
}

export type AutomationConfig = {
  triggerName: string;
  triggerDescription: string;
  step: Steps;
  // True while the AUTOMATIONS_HUB "Edit scope" sub-view is open. When true,
  // CreateTriggerDrawerLoaded renders the "Edit scope" screen instead of the standard step body + footer.
  isEditingScope: boolean;
  createTriggerDrawerInit: CreateTriggerDrawerInit | undefined;
  resetStores: ResetStoresCallback | undefined;
};

const initialAutomationConfig: AutomationConfig = {
  triggerName: '',
  triggerDescription: '',
  step: Steps.EVENT_CONFIGURATION,
  isEditingScope: false,
  createTriggerDrawerInit: undefined,
  resetStores: undefined,
};

export type ResetStoresCallback = () => void;

export const useAutomationStore = create<AutomationConfig>()(
  devtools(
    () => ({
      ...initialAutomationConfig,
    }),
    {name: 'AutomationStore'}
  )
);

const automationSet = useAutomationStore.setState;
const automationGet = useAutomationStore.getState;

export const AutomationStoreActions = {
  init: (
    createTriggerDrawerInit: CreateTriggerDrawerInit,
    resetStores: ResetStoresCallback
  ) => {
    if (createTriggerDrawerInit.isUpdating) {
      const trigger = createTriggerDrawerInit.trigger;
      automationSet({
        triggerName: trigger.name,
        triggerDescription: trigger.description,
        step: Steps.EVENT_CONFIGURATION,
        isEditingScope: false,
        createTriggerDrawerInit,
        resetStores,
      });
    } else {
      automationSet({
        triggerName: '',
        triggerDescription: '',
        step:
          // For workspaces, we really know what the user wants, so we
          // show the simplified flow in that case. We can't show it eg for the Project
          // entry point, because we don't know much about user intent.
          createTriggerDrawerInit.entryPoint === EntryPoint.WORKSPACE_PANEL
            ? Steps.BASIC_METRIC_NOTIFICATION
            : Steps.EVENT_CONFIGURATION,
        isEditingScope: false,
        createTriggerDrawerInit,
        resetStores,
      });
    }
  },
  setTriggerName: (triggerName: string) => automationSet({triggerName}),
  setTriggerDescription: (triggerDescription: string) =>
    automationSet({triggerDescription}),
  setStep: (step: Steps) => automationSet({step}),
  openEditScope: () => automationSet({isEditingScope: true}),
  closeEditScope: () => automationSet({isEditingScope: false}),
  switchToAdvancedConfig: () =>
    automationSet({step: Steps.EVENT_CONFIGURATION}),
  switchToBasicConfig: async () => {
    // When users switch to basic mode, we need to reset all the state
    const state = automationGet();
    if (!state.resetStores) {
      // should never happen since we always set this
      throw new Error('resetStores callback is undefined');
    }

    // Reset all stores using the provided callback
    state.resetStores();

    if (!state.createTriggerDrawerInit) {
      throw new Error('createTriggerDrawerInit is undefined');
    }
    // we need to do this init call here b/c we don't know the resetStores callback until
    // we have created it :)
    AutomationStoreActions.init(
      state.createTriggerDrawerInit,
      state.resetStores
    );
  },
};
